import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, flatMap, catchError } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { BookmarkModel } from './models/bookmark.model';
import { Observable, forkJoin, throwError, of } from 'rxjs';
import { StoryModel } from '../story/models/story.model';
import { SerializeModuleService } from '../edit/serialize-module.service';
import { TrimConfiguration } from 'src/app/edit/models/trim-config.model';
import { ExploreService } from '../explore/explore.service';
import { StoryType } from '../story/models/story-type.enum';
import { endsWith } from 'lodash';
import { Store } from '@ngxs/store';
import { StoryState } from '../story/state/story.state';
import { StoryChaptersState } from '../story/state/chapters/story.chapters.state';
import * as i0 from "@angular/core";
import * as i1 from "@ngxs/store";
import * as i2 from "@angular/common/http";
import * as i3 from "../edit/serialize-module.service";
import * as i4 from "../explore/explore.service";
const bookmarkServiceURL = '/ws/user/bookmark/v1';
const thumbnailServiceURL = '/ws/thumbs/bookmarks';
const favoritesServiceURL = '/ws/user/favorite';
const formHeaders = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8'
    })
};
export var BookmarkCategory;
(function (BookmarkCategory) {
    BookmarkCategory["Private"] = "private";
    BookmarkCategory["Group"] = "enterprise";
    BookmarkCategory["Public"] = "public";
})(BookmarkCategory || (BookmarkCategory = {}));
export class BookmarksService {
    constructor(store, http, serializeModuleService, exploreService) {
        this.store = store;
        this.http = http;
        this.serializeModuleService = serializeModuleService;
        this.exploreService = exploreService;
    }
    /**
     * Gets the bookmark data by the specified id
     * @param id The bookmark id
     */
    getBookmarkData(id) {
        return this.http.get(`/search/bookmarks/data?be=${id}`)
            .pipe(map((result) => {
            result = result.bookmark;
            result.moduleId = result.customData ? result.customData.moduleId : result.modules.activeModules[0];
            return plainToClass(StoryModel, result);
        }));
    }
    /**
     * Gets the bookmark model data by specified id
     * @param id The bookmark id
     */
    getBookmarkModel(id) {
        return this.http.get(`/ws/search/bookmarks?eid=${id}`)
            .pipe(map((result) => plainToClass(BookmarkModel, result.bookmark_info)), map((bookmark) => this.normalizeBookmarkModel(bookmark)));
    }
    /** Gets all user generated bookmarks by specified category
     * @param category Category of bookmarks
     * @param query Optional search query
     */
    getUserBookmarks(category, query = '') {
        return this.http.get(`/ws/search/bookmarks/${category}?q=${query}`)
            .pipe(map((results) => {
            return results
                .map((result) => plainToClass(BookmarkModel, result))
                .map((bookmark) => this.normalizeBookmarkModel(bookmark));
        }));
    }
    /**
     * Gets a list of user mapped custom conditions.
     */
    getUserConditions(category, id) {
        let moduleAccessorLevel = '';
        switch (category) {
            case BookmarkCategory.Private:
                moduleAccessorLevel = 'user';
                break;
            case BookmarkCategory.Group:
                moduleAccessorLevel = 'group';
                break;
            default:
                break;
        }
        return this.http.get(`/ws/search/conditions/private`)
            .pipe(
        // restrict by type + id
        map((results) => {
            return results
                .filter((result) => {
                const matchedByType = result.module_accessor_level === moduleAccessorLevel;
                const matchedById = !id || result.module_accessor_id === id;
                return matchedByType && matchedById;
            });
        }), 
        // to bookmarks
        map((results) => plainToClass(BookmarkModel, results)), 
        // normalize
        map((bookmarks) => {
            return bookmarks
                .map((bookmark) => this.normalizeBookmarkModel(bookmark));
        }));
    }
    /**
     * Gets a list of private bookmarks only accessible to user
     * @param query Optional search query
     */
    getPrivateBookmarks(query = '') {
        return this.getUserBookmarks(BookmarkCategory.Private, query)
            .pipe(map((bookmarks) => {
            return bookmarks.filter((bookmark) => bookmark.is_owner);
        }));
    }
    /**
     * Gets a list of user favorited content, by favorite id.
     */
    getFavorites() {
        return this.http.get(favoritesServiceURL)
            .pipe(map((results) => {
            return Object.values(results)
                .map((result) => plainToClass(BookmarkModel, result))
                .map((bookmark) => this.normalizeBookmarkModel(bookmark));
        }));
    }
    /**
     * Deletes the specified content in the user's favorites.
     */
    deleteFavorite(contentId, contentType) {
        return this.http.delete(`${favoritesServiceURL}?modelId=${contentId}&category=${contentType}`);
    }
    /* Gets a list of user custom conditions.
    */
    getPrivateConditions() {
        return this.getUserConditions(BookmarkCategory.Private);
    }
    /**
     * Gets a list of bookmarks for the specified organziation
     * @param organizationId The organization id for the bookmarks
     * @param query: Optional search query
     */
    getOrganizationBookmarks(organizationId, query = '') {
        return this.getUserBookmarks(BookmarkCategory.Private, query)
            .pipe(map((bookmarks) => {
            // filter enterprise bookmarks
            return bookmarks
                .filter((bookmark) => bookmark.tags.includes('enterprise'))
                // restrict to group accessibility
                .filter((bookmark) => bookmark.group_access.some((access) => access.group_id === organizationId));
        }));
    }
    /**
   * Gets a list of group custom conditions.
   */
    getOrganizationConditions(organizationId) {
        return this.getUserConditions(BookmarkCategory.Group, organizationId);
    }
    /**
     * Gets all content (bookmarks, modules) for the specified organization
     */
    getOrganizationContent(organizationId, query = '') {
        return forkJoin(this.getOrganizationBookmarks(organizationId, query), this.getOrganizationConditions(organizationId))
            .pipe(map(([bookmarks, conditions]) => [...bookmarks, ...conditions]));
    }
    // create a new bookmark object
    createBookmarkStory(inplace = false) {
        return this.serializeModuleService.serialize(inplace, {}, new TrimConfiguration(), false, this.store.selectSnapshot(StoryState.getStory), this.store.selectSnapshot(StoryChaptersState.getStoryChapters));
    }
    /**
     * Gets a list of public bookmarks
     * @param query: Optional search query
     */
    getPublicBookmarks(query = '') {
        return this.getUserBookmarks(BookmarkCategory.Public, query);
    }
    saveBookmark(entry) {
        // normalize to required service params
        const normalizedEntry = this.getNormalizedBookmarkEntry(entry);
        let response$;
        if (entry.id) {
            // update bookmark
            response$ = this.http.post(`${bookmarkServiceURL}/update`, normalizedEntry, formHeaders);
        }
        else {
            // save new bookmark
            response$ = this.http.post(`${bookmarkServiceURL}/save`, normalizedEntry, formHeaders);
        }
        return response$.pipe(
        // normalize to bookmark model
        flatMap((response) => this.getBookmarkModel(response.id)));
    }
    deleteBookmark(id) {
        return this.http.delete(`${bookmarkServiceURL}/delete?eid=${id}`)
            .pipe(catchError((response) => {
            const { error } = response.error;
            const message = error ? error.message : '';
            return throwError(message);
        }));
    }
    copyBookmark(id, type) {
        // Fix module id truncation
        if (type === StoryType.Module && !endsWith(id, '.json')) {
            id += '.json';
        }
        return this.http.post(`${bookmarkServiceURL}/copy`, { source_content_type: type, source_content_id: id })
            .pipe(map((response) => response.id));
    }
    atMaxFreeBookmarkCount() {
        return this.http.get(`/ws/user/content/limit/check`)
            .pipe(map(() => false), catchError((response) => of(response.status === 403)));
    }
    // save thumbnail data for each chapter in a bookmark
    saveBookmarkThumbnails(id, bookmarkEntry, thumbnails, chapterIdMap) {
        // chapter thumbnails
        const thumbnailRequests$ = bookmarkEntry.index.chapters.reduce((requests, chapter) => {
            const newChapterId = chapter.id;
            const oldChapterId = chapterIdMap[newChapterId];
            const currentThumbnail = thumbnails.get(oldChapterId);
            if (currentThumbnail && currentThumbnail.snapshot) {
                requests.push(this.postThumbnail(id, chapter.id, currentThumbnail.snapshot));
            }
            else if (chapter.id !== chapterIdMap[chapter.id] || currentThumbnail.chapterId !== chapter.id) {
                const splitIdx = currentThumbnail.urls.large.match(/(bookmarks\/)|(modules\/)/).index;
                const srcURL = currentThumbnail.urls.large.slice(splitIdx);
                requests.push(this.copyThumbnail(id, chapter.id, srcURL));
            }
            return requests;
        }, []);
        // bookmark gallery thumbnail
        const firstChapterThumbnail = thumbnails.get(chapterIdMap[bookmarkEntry.index.chapters[0].id]);
        if (firstChapterThumbnail.snapshot) {
            // create new
            thumbnailRequests$.push(this.postThumbnail(id, null, firstChapterThumbnail.snapshot));
        }
        else {
            // copy existing
            thumbnailRequests$.push(this.copyThumbnail(id, null, firstChapterThumbnail.urls.large));
        }
        return thumbnailRequests$;
    }
    // delete thumbnails for deleted chapters
    deleteBookmarkThumbnails(id, bookmarkEntry, thumbnails, chapterIdMap) {
        /*
        delete existing (server stored) thumbnail urls on deleted chapters
        */
        const chapterIds = bookmarkEntry.index.chapters.map((chapter) => chapterIdMap[chapter.id] || chapter.id);
        const deleteRequests$ = Array.from(thumbnails.values()).filter((thumbnail) => chapterIds.indexOf(thumbnail.chapterId) < 0)
            .map((thumbnail) => {
            return this.deleteThumbnail(id, thumbnail.chapterId);
        });
        return deleteRequests$;
    }
    /**
     * Filters the specified list by tags
     * @param bookmarks The list of bookmarks
     * @param tags The list of tags
     * @param inclusive True to match all tags, else single match
     */
    filterByTags(bookmarks, tags, inclusive = false) {
        return bookmarks.filter((bookmark) => {
            if (inclusive) {
                return tags.every((tag) => bookmark.tags.includes(tag));
            }
            else {
                return tags.some((tag) => bookmark.tags.includes(tag));
            }
        });
    }
    canEdit(bookmark) {
        if (bookmark.is_owner) {
            return true;
        }
        if (bookmark.group_access && bookmark.group_access.length) {
            return bookmark.group_access[0].member_edit;
        }
        return false;
    }
    normalizeBookmarkModel(bookmark) {
        const { id, type } = bookmark;
        // normalize id
        if (type === 'module') {
            bookmark.id = this.exploreService.toAppContentId(id);
        }
        // normalize tag properties
        bookmark.tour = bookmark.tags.includes('tour');
        bookmark.animated = bookmark.tags.includes('animated');
        return bookmark;
    }
    getNormalizedBookmarkEntry(entry) {
        const { id, title, description, index, public_access, add_group_access, remove_group_access, isOwner } = entry;
        // optional user (owner) fields
        let group_access;
        let publicAccess;
        let flags;
        if (isOwner) {
            // set additional group access    
            group_access = add_group_access ? this.getGroupAccess(add_group_access) : [];
            // revoke group access
            if (remove_group_access) {
                remove_group_access.forEach((access) => {
                    const removedAccess = this.getNormalizeGroupAccess({
                        group_id: access.group_id,
                        remove: true
                    });
                    group_access.push(removedAccess);
                });
            }
            // set public access
            publicAccess = this.getNormalizedBool(public_access);
            // custom flags
            if (index) {
                // init flags
                flags = {};
                // flag: user generated tours
                if (index.chapters) {
                    const isTour = index.chapters.length > 1;
                    flags['user_generated_tour'] = this.getNormalizedBool(isTour);
                }
                // flag: animated content
                const isAnimated = index.tags.includes('animated');
                flags['animated'] = this.getNormalizedBool(isAnimated);
            }
        }
        // normalized service entry
        return {
            eid: id,
            name: title,
            description: description,
            // serialized story
            index: index,
            // permissions: set null for no updates
            group_access: group_access,
            public_access: publicAccess,
            // custom bookmark flags
            flags: flags
        };
    }
    getGroupAccess(groupAccess) {
        return groupAccess.map((access) => this.getNormalizeGroupAccess(access));
    }
    getNormalizeGroupAccess(access) {
        const { group_id, group_name, member_edit, visible, remove } = access;
        const normalized = {
            group_id: group_id,
            group_name: group_name,
            member_edit: this.getNormalizedBool(member_edit),
            visibility: this.getNormalizedBool(visible),
            remove: this.getNormalizedBool(remove)
        };
        return normalized;
    }
    getNormalizedBool(value) {
        return value === true ? '1' : (value === false ? '0' : '');
    }
    // request to server to save a thumbnail
    postThumbnail(storyId, chapterId, thumbnailURL) {
        const image = new Image();
        image.src = thumbnailURL;
        return new Observable((observer) => {
            image.onload = () => {
                // transfer image to canvas
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.width = image.naturalWidth;
                canvas.height = image.naturalHeight;
                context.drawImage(image, 0, 0);
                // convert to data url
                const dataURL = canvas.toDataURL('image/jpeg', 1.0);
                // post request for thumbnail
                let _thumbnailURL = '';
                if (chapterId) {
                    // chapter thumbnail
                    _thumbnailURL = `${thumbnailServiceURL}/${storyId}/large/chapters/${chapterId}.jpg`;
                }
                else {
                    // story thumbnail
                    _thumbnailURL = `${thumbnailServiceURL}/${storyId}/large/index.jpg`;
                }
                const headers = new HttpHeaders().set('Content-Type', 'application/json');
                this.http.post(_thumbnailURL, { image: dataURL }, { headers: headers, responseType: 'text' }).subscribe((r) => {
                    observer.next();
                    observer.complete();
                });
            };
        });
    }
    // request to server to delete a thumbnail
    deleteThumbnail(storyId, chapterId) {
        return new Observable((observer) => {
            const request = new Request(`${thumbnailServiceURL}/${storyId}/large/chapters/${chapterId}.jpg`, { method: 'DELETE' });
            fetch(request).then((response) => {
                observer.complete();
            });
        });
    }
    // request to server to copy a thumbnail from one location to another
    copyThumbnail(storyId, chapterId, src) {
        let dst = '';
        const srcURL = src.replace('/thumbs/', ''); // 
        if (chapterId) {
            // copy to chapter location
            dst = `bookmarks/${storyId}/large/chapters/${chapterId}.jpg`;
        }
        else {
            // copy to index location
            dst = `bookmarks/${storyId}/large/index.jpg`;
        }
        const header = new HttpHeaders().set('Content-Type', 'application/json');
        return new Observable((observer) => {
            this.http.post('/ws/thumbs/copy', { src: srcURL, dst: dst }, { headers: header, responseType: 'text' }).subscribe((r) => {
                observer.next();
                observer.complete();
            });
        });
    }
}
BookmarksService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function BookmarksService_Factory() { return new BookmarksService(i0.ɵɵinject(i1.Store), i0.ɵɵinject(i2.HttpClient), i0.ɵɵinject(i3.SerializeModuleService), i0.ɵɵinject(i4.ExploreService)); }, token: BookmarksService, providedIn: "root" });
